import React, { useState } from 'react'
import { supabase } from '../supabaseClient'

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [username, setUsername] = useState('')
  const [afibStatus, setAfibStatus] = useState('confirmed')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [mode, setMode] = useState('login')
  const [message, setMessage] = useState(null)

  async function handleLogin(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setError(error.message)
    setLoading(false)
  }

  async function handleSignUp(e) {
    e.preventDefault()
    setLoading(true)
    setError(null)
    if (!username.trim()) {
      setError('Please choose a username')
      setLoading(false)
      return
    }
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) {
      setError(error.message)
    } else {
      if (data?.user) {
        await supabase.from('users').upsert({
          id: data.user.id,
          email: email,
          username: username.trim(),
          afib_confirmed: afibStatus
        })
      }
      setMessage('Account created! Please check your email to confirm your account, then sign in below.')
      setMode('login')
      setEmail('')
      setPassword('')
      setUsername('')
      setAfibStatus('confirmed')
    }
    setLoading(false)
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: '#f0f4f8', fontFamily: 'Arial'
    }}>
      <div style={{
        background: 'white', borderRadius: 16, padding: '40px 36px',
        width: '100%', maxWidth: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.08)'
      }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>❤️</div>
          <h1 style={{ color: '#185FA5', fontSize: 24, fontWeight: 700, margin: 0 }}>
            MyAFib Control
          </h1>
          <p style={{ color: '#666', fontSize: 14, marginTop: 6 }}>
            Heart Rhythm Center
          </p>
        </div>

        {/* Mode toggle tabs */}
        <div style={{ display: 'flex', marginBottom: 24, borderRadius: 10, overflow: 'hidden', border: '1px solid #ddd' }}>
          <button
            onClick={() => { setMode('login'); setError(null); setMessage(null) }}
            style={{
              flex: 1, padding: '10px', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              background: mode === 'login' ? '#185FA5' : 'white',
              color: mode === 'login' ? 'white' : '#888'
            }}>
            Sign in
          </button>
          <button
            onClick={() => { setMode('signup'); setError(null); setMessage(null) }}
            style={{
              flex: 1, padding: '10px', border: 'none', cursor: 'pointer',
              fontSize: 14, fontWeight: 600,
              background: mode === 'signup' ? '#1D9E75' : 'white',
              color: mode === 'signup' ? 'white' : '#888'
            }}>
            Create account
          </button>
        </div>

        {/* Mode description */}
        {mode === 'signup' && (
          <div style={{ background: '#E6F1FB', borderRadius: 8, padding: '10px 14px', marginBottom: 16, fontSize: 13, color: '#0C447C' }}>
            👋 Welcome! Create your free account to start tracking your AFib control score.
          </div>
        )}

        {error && (
          <div style={{
            background: '#FCEBEB', color: '#A32D2D', padding: '10px 14px',
            borderRadius: 8, marginBottom: 16, fontSize: 14
          }}>
            {error}
          </div>
        )}

        {message && (
          <div style={{
            background: '#E1F5EE', color: '#085041', padding: '12px 14px',
            borderRadius: 8, marginBottom: 16, fontSize: 14, lineHeight: 1.6
          }}>
            ✅ {message}
          </div>
        )}

        <form onSubmit={mode === 'login' ? handleLogin : handleSignUp}>

          {/* Signup only fields */}
          {mode === 'signup' && (
            <div>
              {/* Username */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>
                  Choose a username
                </label>
                <input
                  type="text"
                  value={username}
                  onChange={e => setUsername(e.target.value)}
                  required
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                    border: '1px solid #ddd', boxSizing: 'border-box', outline: 'none'
                  }}
                  placeholder="e.g. JLW2026 (no real name needed)"
                />
                <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                  Your username is the only name stored — no real names required.
                </div>
              </div>

              {/* AFib status */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 8 }}>
                  AFib diagnosis status
                </label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[
                    { value: 'confirmed', label: '✅ Yes — diagnosed by a physician', desc: 'I have a confirmed AFib diagnosis from a healthcare provider.' },
                    { value: 'suspected', label: '⚠️ I think I might have AFib', desc: 'I have symptoms but have not been formally diagnosed yet.' },
                    { value: 'educational', label: '📖 No — using for educational purposes', desc: 'I do not have AFib but want to learn about it.' },
                  ].map(option => (
                    <div key={option.value}
                      onClick={() => setAfibStatus(option.value)}
                      style={{
                        padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                        border: afibStatus === option.value ? '2px solid #185FA5' : '1px solid #ddd',
                        background: afibStatus === option.value ? '#E6F1FB' : 'white'
                      }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: afibStatus === option.value ? '#185FA5' : '#2C2C2C', marginBottom: 2 }}>
                        {option.label}
                      </div>
                      <div style={{ fontSize: 11, color: '#666' }}>{option.desc}</div>
                    </div>
                  ))}
                </div>
                {afibStatus === 'suspected' && (
                  <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: '#FAEEDA', fontSize: 12, color: '#633806', lineHeight: 1.6 }}>
                    ⚠️ If you suspect you have AFib, please see a physician for a proper evaluation before using the tracking features. AFib requires a formal diagnosis with an ECG or rhythm strip. You can still use the educational content while you seek care.
                  </div>
                )}
                {afibStatus === 'educational' && (
                  <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 8, background: '#E6F1FB', fontSize: 12, color: '#0C447C', lineHeight: 1.6 }}>
                    ℹ️ The check-in and scoring features are designed for people with confirmed AFib. You are welcome to explore the app and educational content — just keep in mind the scores are most meaningful for confirmed AFib patients.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Email */}
          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>
              Email address
            </label>
            <input
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                border: '1px solid #ddd', boxSizing: 'border-box', outline: 'none'
              }}
              placeholder="you@example.com"
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 13, color: '#555', display: 'block', marginBottom: 4 }}>
              {mode === 'login' ? 'Password' : 'Create a password'}
            </label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              style={{
                width: '100%', padding: '10px 12px', borderRadius: 8, fontSize: 14,
                border: '1px solid #ddd', boxSizing: 'border-box', outline: 'none'
              }}
              placeholder={mode === 'login' ? '••••••••' : 'At least 8 characters'}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%', padding: '12px', borderRadius: 8, border: 'none',
              background: loading ? '#9CB8D9' : mode === 'signup' ? '#1D9E75' : '#185FA5',
              color: 'white', fontSize: 15, fontWeight: 600,
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign in to my account' : 'Create my account'}
          </button>
        </form>

        {mode === 'login' && (
          <p style={{ textAlign: 'center', marginTop: 16, fontSize: 13, color: '#666' }}>
            New to MyAFib Control?{' '}
            <span
              onClick={() => { setMode('signup'); setError(null); setMessage(null) }}
              style={{ color: '#185FA5', cursor: 'pointer', fontWeight: 600 }}>
              Create a free account
            </span>
          </p>
        )}

        <p style={{
          textAlign: 'center', marginTop: 24, fontSize: 11, color: '#999',
          borderTop: '1px solid #eee', paddingTop: 16
        }}>
          This app provides educational information only — not medical advice.
        </p>

        <p style={{
          textAlign: 'center', marginTop: 10, fontSize: 11, color: '#185FA5',
          background: '#E6F1FB', borderRadius: 8, padding: '8px 12px'
        }}>
          ℹ️ For the best experience, please disable private browsing and ensure cookies are enabled in your browser settings.
        </p>

        <div style={{
          marginTop: 12, padding: '10px 12px', borderRadius: 8,
          background: '#F5F5F5', fontSize: 11, color: '#666', lineHeight: 1.7
        }}>
          <strong style={{ color: '#444' }}>Privacy & Data Notice</strong><br />
          MyAFib Control is an educational wellness tracking tool developed by
          Heart Rhythm Center. It is not a clinical service and does not establish
          a patient-physician relationship. Your health lifestyle data is stored
          securely, never sold, and never shared with third parties. Only
          anonymized data is used for coaching personalization. No real names
          are stored — your username is your only identifier.<br /><br />
          By creating an account you agree that this tool is for educational
          purposes only and is not a substitute for professional medical care.
        </div>

        <p style={{
          textAlign: 'center', marginTop: 10, fontSize: 10, color: '#999'
        }}>
          © 2026 Jeffrey L. Williams, MD. All rights reserved.
        </p>

      </div>
    </div>
  )
}